import { useRef } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { FiPrinter, FiX, FiShield, FiCheckCircle, FiFileText, FiUser, FiCalendar, FiSmartphone } from 'react-icons/fi';

const LegalAcceptanceModal = ({ isOpen, onClose, app, user }) => {
  const { isDarkMode } = useTheme();
  const printRef = useRef(null);

  if (!isOpen || !app) return null;

  const legal = app.legalAcceptance || {};
  const userName = user?.fullName || legal.userName || 'Cliente CriaApp';
  const userCpf = user?.cpf || 'Cadastrado no sistema';
  const userEmail = user?.email || app.userEmail || 'Não informado';
  const userPhone = user?.phone || 'Não informado';
  const userId = app.userId || user?.uid || user?.id || 'ID não localizado';

  // Formatação de data
  const getAcceptanceDate = () => {
    if (legal.acceptedAt) {
      try {
        return new Date(legal.acceptedAt).toLocaleString('pt-BR', {
          dateStyle: 'full',
          timeStyle: 'medium'
        });
      } catch (e) {
        return legal.acceptedAt;
      }
    }
    if (app.termsAcceptedAt) {
      try {
        const d = app.termsAcceptedAt.toDate ? app.termsAcceptedAt.toDate() : new Date(app.termsAcceptedAt.seconds ? app.termsAcceptedAt.seconds * 1000 : app.termsAcceptedAt);
        return d.toLocaleString('pt-BR', { dateStyle: 'full', timeStyle: 'medium' });
      } catch (e) {
        return 'Data registrada no sistema';
      }
    }
    if (app.createdAt) {
      try {
        const d = app.createdAt.toDate ? app.createdAt.toDate() : new Date(app.createdAt.seconds ? app.createdAt.seconds * 1000 : app.createdAt);
        return d.toLocaleString('pt-BR', { dateStyle: 'full', timeStyle: 'medium' });
      } catch (e) {
        return String(app.createdAt);
      }
    }
    return new Date().toLocaleString('pt-BR', { dateStyle: 'full', timeStyle: 'medium' });
  };

  const formattedDate = getAcceptanceDate();
  const documentHash = legal.documentHash || `ACEITE-${(app.id || 'APP').substring(0, 8).toUpperCase()}-${(userId || 'USR').substring(0, 6).toUpperCase()}`;
  const ipAddress = legal.ipAddress || 'Registrado via Web Client';
  const userAgent = legal.userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : 'Navegador Web do Cliente');
  const contractVersion = legal.contractVersion || 'v1.0-2025';

  // 🔥 IMPRESSÃO DIRETA VIA IFRAME ISOLADO (Evita bugs de página em branco no Chrome / React)
  const handlePrint = () => {
    try {
      // 1. Remove qualquer iframe de impressão residual
      const existingIframe = document.getElementById('legal-print-iframe');
      if (existingIframe) {
        existingIframe.remove();
      }

      // 2. Cria o iframe isolado
      const iframe = document.createElement('iframe');
      iframe.id = 'legal-print-iframe';
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.style.visibility = 'hidden';
      document.body.appendChild(iframe);

      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(`
        <!DOCTYPE html>
        <html lang="pt-BR">
          <head>
            <meta charset="utf-8" />
            <title>Comprovante de Aceite - ${app.name || 'App'}</title>
            <style>
              @page {
                size: A4 portrait;
                margin: 12mm 15mm;
              }
              * {
                box-sizing: border-box;
                margin: 0;
                padding: 0;
                font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
              }
              body {
                background: #ffffff !important;
                color: #111827 !important;
                padding: 10px;
                font-size: 11px;
                line-height: 1.45;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              .header {
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                border-bottom: 2px solid #111827;
                padding-bottom: 12px;
                margin-bottom: 14px;
              }
              .brand {
                font-size: 22px;
                font-weight: 900;
                letter-spacing: -0.5px;
                text-transform: uppercase;
                color: #1e3a8a;
              }
              .tag {
                display: inline-block;
                font-size: 9px;
                font-weight: bold;
                background: #dbeafe;
                color: #1e40af;
                padding: 2px 6px;
                border-radius: 4px;
                margin-left: 6px;
                vertical-align: middle;
              }
              .confirmed-badge {
                display: inline-block;
                background: #dcfce7;
                border: 1px solid #86efac;
                color: #14532d;
                font-weight: 700;
                font-size: 10px;
                padding: 3px 8px;
                border-radius: 9999px;
              }
              .title-box {
                text-align: center;
                margin-bottom: 14px;
              }
              .title-box h1 {
                font-size: 15px;
                font-weight: 800;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                color: #111827;
              }
              .title-box p {
                font-size: 10.5px;
                color: #4b5563;
                margin-top: 3px;
              }
              .legal-ref {
                margin-top: 6px;
                font-size: 9.5px;
                color: #4b5563;
                font-style: italic;
                background: #f9fafb;
                border: 1px solid #e5e7eb;
                padding: 5px 10px;
                border-radius: 6px;
              }
              .grid-2 {
                display: flex;
                gap: 10px;
                margin-bottom: 12px;
              }
              .col {
                flex: 1;
                border: 1px solid #d1d5db;
                border-radius: 8px;
                padding: 10px;
                background: #fdfdfd;
              }
              .col-title {
                font-size: 10px;
                font-weight: 800;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                color: #1e3a8a;
                margin-bottom: 6px;
                border-bottom: 1px solid #e5e7eb;
                padding-bottom: 3px;
              }
              .col-text {
                font-size: 10.5px;
                color: #1f2937;
                line-height: 1.5;
              }
              .box {
                border: 1px solid #d1d5db;
                border-radius: 8px;
                padding: 10px;
                margin-bottom: 12px;
              }
              .box-title {
                font-size: 10px;
                font-weight: 800;
                text-transform: uppercase;
                color: #1e3a8a;
                margin-bottom: 6px;
                border-bottom: 1px solid #e5e7eb;
                padding-bottom: 3px;
              }
              .specs-grid {
                display: flex;
                flex-wrap: wrap;
                gap: 6px;
              }
              .spec-item {
                flex: 1;
                min-width: 120px;
                background: #f9fafb;
                border: 1px solid #e5e7eb;
                padding: 6px;
                border-radius: 5px;
              }
              .spec-label {
                font-size: 8.5px;
                text-transform: uppercase;
                font-weight: 700;
                color: #6b7280;
                display: block;
              }
              .spec-val {
                font-size: 10.5px;
                font-weight: 700;
                color: #111827;
              }
              .spec-url {
                width: 100%;
                background: #f9fafb;
                border: 1px solid #e5e7eb;
                padding: 6px;
                border-radius: 5px;
                font-size: 9.5px;
                font-family: monospace;
                word-break: break-all;
              }
              .clauses {
                border: 1px solid #bfdbfe;
                background: #eff6ff;
                border-radius: 8px;
                padding: 10px;
                margin-bottom: 12px;
              }
              .clauses-title {
                font-size: 10px;
                font-weight: 800;
                text-transform: uppercase;
                color: #1e3a8a;
                margin-bottom: 5px;
              }
              .clauses ul {
                font-size: 9.5px;
                color: #1f2937;
                line-height: 1.45;
                padding-left: 14px;
              }
              .clauses li {
                margin-bottom: 3px;
              }
              .audit {
                border: 2px solid #111827;
                background: #f9fafb;
                border-radius: 8px;
                padding: 10px;
                margin-bottom: 14px;
              }
              .audit-title {
                font-size: 10px;
                font-weight: 900;
                text-transform: uppercase;
                color: #111827;
                margin-bottom: 6px;
              }
              .audit-grid {
                display: flex;
                flex-wrap: wrap;
                gap: 8px;
                font-size: 10px;
              }
              .audit-item {
                flex: 1;
                min-width: 160px;
              }
              .audit-item span {
                font-size: 8.5px;
                text-transform: uppercase;
                color: #6b7280;
                font-weight: 700;
                display: block;
              }
              .audit-hash {
                width: 100%;
                border-top: 1px solid #e5e7eb;
                padding-top: 5px;
                margin-top: 3px;
                font-family: monospace;
                font-size: 9.5px;
                color: #1e3a8a;
                font-weight: 700;
              }
              .footer {
                text-align: center;
                border-top: 1px solid #d1d5db;
                padding-top: 10px;
                font-size: 9px;
                color: #6b7280;
                text-transform: uppercase;
                letter-spacing: 0.5px;
              }
            </style>
          </head>
          <body>
            <div class="header">
              <div>
                <div>
                  <span class="brand">CriaApp</span>
                  <span class="tag">Tecnologia SaaS</span>
                </div>
                <p style="font-size: 10.5px; color: #4b5563; margin-top: 2px;">CriaApp Soluções Tecnológicas &middot; criaapp.com</p>
                <p style="font-size: 9.5px; color: #6b7280;">Suporte Jurídico e Operacional: +55 (82) 99699-2182</p>
              </div>
              <div style="text-align: right;">
                <span class="confirmed-badge">✓ ACEITE CONFIRMADO</span>
                <p style="font-size: 9.5px; font-family: monospace; color: #4b5563; margin-top: 4px;">Hash: ${documentHash}</p>
                <p style="font-size: 9px; color: #6b7280;">Versão: ${contractVersion}</p>
              </div>
            </div>

            <div class="title-box">
              <h1>Certificado de Contratação e Aceite Eletrônico</h1>
              <p>
                Comprovante de manifestação expressa de vontade e adesão irretratável ao <strong>Contrato de Prestação de Serviços Tecnológicos, Conversão em Aplicativo Móvel e Licenciamento Contínuo</strong>.
              </p>
              <div class="legal-ref">
                Emitido nos termos do <strong>Art. 10, § 2º da Medida Provisória nº 2.200-2/2001</strong>, da <strong>Lei Federal nº 14.063/2020</strong> e dos <strong>Artigos 427 e seguintes do Código Civil Brasileiro</strong>.
              </div>
            </div>

            <div class="grid-2">
              <div class="col">
                <div class="col-title">CONTRATADA (Prestadora)</div>
                <div class="col-text">
                  <p><strong>Razão:</strong> CRIAAPP SOLUÇÕES TECNOLÓGICAS</p>
                  <p><strong>Plataforma:</strong> Plataforma SaaS CriaApp (criaapp.com)</p>
                  <p><strong>Canal Oficial:</strong> WhatsApp +55 82 99699-2182</p>
                  <p><strong>Objeto:</strong> Conversão e publicação de app móvel</p>
                </div>
              </div>

              <div class="col">
                <div class="col-title">CONTRATANTE (Usuário Titular)</div>
                <div class="col-text">
                  <p><strong>Nome:</strong> ${userName}</p>
                  <p><strong>E-mail:</strong> ${userEmail}</p>
                  <p><strong>CPF:</strong> ${userCpf}</p>
                  <p><strong>Telefone:</strong> ${userPhone}</p>
                  <p style="font-family: monospace; font-size: 9.5px;"><strong>ID do Usuário (UID):</strong> ${userId}</p>
                </div>
              </div>
            </div>

            <div class="box">
              <div class="box-title">Especificações do Aplicativo e Pedido</div>
              <div class="specs-grid">
                <div class="spec-item">
                  <span class="spec-label">Nome do App</span>
                  <span class="spec-val">${app.name || 'Meu App'}</span>
                </div>
                <div class="spec-item">
                  <span class="spec-label">ID do Pedido</span>
                  <span class="spec-val" style="font-family: monospace; font-size: 9.5px;">${app.id}</span>
                </div>
                <div class="spec-item">
                  <span class="spec-label">Plataformas</span>
                  <span class="spec-val">${app.platforms?.join(', ') || 'Android'}</span>
                </div>
                <div class="spec-item">
                  <span class="spec-label">Mensalidade</span>
                  <span class="spec-val" style="color: #15803d;">R$ ${Number(app.totalPrice || 0).toFixed(2)}</span>
                </div>
                <div class="spec-url">
                  <strong>URL Website:</strong> ${app.url || 'Não informada'}
                </div>
              </div>
            </div>

            <div class="clauses">
              <div class="clauses-title">Cláusulas Especiais e Obrigações Contratuais Assumidas</div>
              <ul>
                <li>
                  <strong>Fidelidade Contratual Mínima de 12 Meses:</strong> O CONTRATANTE reconhece expressamente que o contrato vige pelo prazo determinado de 12 (doze) meses de fidelidade, iniciado na confirmação do pedido, em razão dos investimentos operacionais e custos de compilação assumidos pela CONTRATADA.
                </li>
                <li>
                  <strong>Multa Penal Compensatória (30%):</strong> O cancelamento voluntário ou rescisão imotivada antes dos 12 meses sujeitará o mesmo à multa rescisória de 30% (trinta por cento) sobre a soma das mensalidades remanescentes.
                </li>
                <li>
                  <strong>Vedação Expressa de Chargebacks:</strong> É vedada a abertura de contestações bancárias injustificadas (*chargebacks*) após início do desenvolvimento, sujeitando o devedor à cobrança integral com perdas e danos.
                </li>
                <li>
                  <strong>Foro de Eleição:</strong> Eleito o Foro da Comarca da sede da CONTRATADA para dirimir quaisquer litígios.
                </li>
              </ul>
            </div>

            <div class="audit">
              <div class="audit-title">Trilha de Auditoria Digital (Evidências Eletrônicas de Aceite)</div>
              <div class="audit-grid">
                <div class="audit-item">
                  <span>Data e Hora do Aceite</span>
                  <strong style="color: #111827;">${formattedDate}</strong>
                </div>
                <div class="audit-item">
                  <span>Status da Assinatura</span>
                  <strong style="color: #15803d;">✓ Válida, irretratável e arquivada</strong>
                </div>
                <div class="audit-item">
                  <span>Endereço IP Registrado</span>
                  <strong style="font-family: monospace; color: #1f2937;">${ipAddress}</strong>
                </div>
                <div class="audit-item">
                  <span>Dispositivo / User-Agent</span>
                  <span style="font-family: monospace; font-size: 8.5px; color: #374151; word-break: break-all;">${userAgent}</span>
                </div>
                <div class="audit-hash">
                  Hash Criptográfico de Integridade: <strong>${documentHash}</strong>
                </div>
              </div>
            </div>

            <div class="footer">
              <p>Documento assinado digitalmente por manifestação de vontade expressa do contratante</p>
              <p style="margin-top: 3px; font-size: 8px; color: #9ca3af;">
                Registro sob guarda eletrônica da CriaApp Soluções Tecnológicas &middot; ID Pedido: ${app.id} &middot; ID Cliente: ${userId}
              </p>
            </div>
          </body>
        </html>
      `);
      doc.close();

      // Dispara a impressão no iframe após carregar
      setTimeout(() => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (e) {
          console.error('Erro na impressão pelo iframe:', e);
          window.print();
        }
      }, 350);

    } catch (err) {
      console.error('Erro ao preparar impressão:', err);
      window.print();
    }
  };

  return (
    <div className="fixed inset-0 z-[150] overflow-y-auto bg-black/75 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6">
      <div className={`relative w-full max-w-4xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh] ${
        isDarkMode ? 'bg-gray-900 border border-gray-800 text-white' : 'bg-white border border-gray-200 text-gray-900'
      }`}>

        {/* Barra superior de ações */}
        <div className={`px-6 py-4 flex items-center justify-between border-b ${
          isDarkMode ? 'bg-gray-800/80 border-gray-700' : 'bg-gray-50 border-gray-200'
        }`}>
          <div className="flex items-center gap-2">
            <div className="p-2 bg-blue-100 text-blue-700 rounded-lg">
              <FiShield className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-base sm:text-lg">Comprovante de Aceite Eletrônico</h3>
              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Documento probatório para fins jurídicos e administrativos
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl shadow-md transition-all active:scale-95"
            >
              <FiPrinter className="w-4 h-4" />
              <span>Imprimir / Salvar PDF</span>
            </button>
            <button
              onClick={onClose}
              className={`p-2 rounded-xl transition-colors ${
                isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'
              }`}
              title="Fechar"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Visualização do Certificado na Tela */}
        <div ref={printRef} className="p-6 sm:p-10 overflow-y-auto space-y-6 text-gray-900 bg-white">
          
          {/* Cabeçalho do Documento */}
          <div className="border-b-2 border-gray-900 pb-5 text-center sm:text-left flex flex-col sm:flex-row justify-between items-center gap-4">
            <div>
              <div className="flex items-center justify-center sm:justify-start gap-2">
                <span className="text-2xl font-black tracking-tight text-blue-900 uppercase">CriaApp</span>
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded font-bold uppercase tracking-wider">
                  Tecnologia SaaS
                </span>
              </div>
              <p className="text-xs text-gray-600 mt-1">CriaApp Soluções Tecnológicas &middot; criaapp.com</p>
              <p className="text-[11px] text-gray-500">Suporte Jurídico e Operacional: +55 (82) 99699-2182</p>
            </div>

            <div className="text-center sm:text-right border-t sm:border-t-0 pt-2 sm:pt-0 border-gray-200">
              <span className="inline-block px-3 py-1 bg-green-100 border border-green-300 text-green-900 font-bold text-xs rounded-full">
                ✓ ACEITE CONFIRMADO
              </span>
              <p className="text-[10px] font-mono text-gray-600 mt-1">Hash: {documentHash}</p>
              <p className="text-[10px] text-gray-500">Versão do Contrato: {contractVersion}</p>
            </div>
          </div>

          {/* Título Oficial */}
          <div className="text-center py-2">
            <h1 className="text-xl sm:text-2xl font-extrabold text-gray-900 uppercase tracking-wide">
              Certificado de Contratação e Aceite Eletrônico
            </h1>
            <p className="text-xs text-gray-600 max-w-2xl mx-auto mt-1 leading-relaxed">
              Comprovante de manifestação expressa de vontade e adesão irretratável ao <strong>Contrato de Prestação de Serviços Tecnológicos, Conversão em Aplicativo Móvel e Licenciamento Contínuo</strong>.
            </p>
            <div className="mt-2 text-[11px] text-gray-500 italic bg-gray-50 border border-gray-200 rounded-lg p-2 max-w-3xl mx-auto">
              Emitido nos termos do <strong>Art. 10, § 2º da Medida Provisória nº 2.200-2/2001</strong>, da <strong>Lei Federal nº 14.063/2020</strong> (Marco Legal das Assinaturas Eletrônicas) e dos <strong>Artigos 427 e seguintes do Código Civil Brasileiro</strong>.
            </div>
          </div>

          {/* Qualificação das Partes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Contratada */}
            <div className="border border-gray-300 rounded-xl p-4 bg-gray-50/50">
              <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <FiFileText className="text-blue-700" />
                CONTRATADA (Prestadora)
              </h4>
              <div className="text-xs space-y-1 text-gray-800">
                <p><strong>Razão:</strong> CRIAAPP SOLUÇÕES TECNOLÓGICAS</p>
                <p><strong>Plataforma:</strong> Plataforma SaaS CriaApp (criaapp.com)</p>
                <p><strong>Canal Oficial:</strong> WhatsApp +55 82 99699-2182</p>
                <p><strong>Objeto:</strong> Conversão e publicação de app móvel (Android / iOS)</p>
              </div>
            </div>

            {/* Contratante */}
            <div className="border border-gray-300 rounded-xl p-4 bg-gray-50/50">
              <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <FiUser className="text-blue-700" />
                CONTRATANTE (Usuário Titular)
              </h4>
              <div className="text-xs space-y-1 text-gray-800">
                <p><strong>Nome:</strong> {userName}</p>
                <p><strong>E-mail:</strong> {userEmail}</p>
                <p><strong>CPF:</strong> {userCpf}</p>
                <p><strong>Telefone:</strong> {userPhone}</p>
                <p className="font-mono text-[11px]"><strong>ID do Usuário (UID):</strong> {userId}</p>
              </div>
            </div>
          </div>

          {/* Dados do Aplicativo / Contratação */}
          <div className="border border-gray-300 rounded-xl p-4">
            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <FiSmartphone className="text-blue-700" />
              Especificações do Aplicativo e Pedido
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-200">
                <span className="text-[10px] uppercase text-gray-500 font-bold block">Nome do App</span>
                <span className="font-bold text-gray-900 truncate block">{app.name || 'Meu App'}</span>
              </div>
              <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-200">
                <span className="text-[10px] uppercase text-gray-500 font-bold block">ID do Pedido</span>
                <span className="font-mono text-gray-900 truncate block">{app.id}</span>
              </div>
              <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-200">
                <span className="text-[10px] uppercase text-gray-500 font-bold block">Plataformas</span>
                <span className="font-bold text-gray-900">{app.platforms?.join(', ') || 'Android'}</span>
              </div>
              <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-200">
                <span className="text-[10px] uppercase text-gray-500 font-bold block">Valor da Mensalidade</span>
                <span className="font-bold text-green-700">R$ {Number(app.totalPrice || 0).toFixed(2)}</span>
              </div>
              <div className="col-span-2 sm:col-span-4 bg-gray-50 p-2.5 rounded-lg border border-gray-200">
                <span className="text-[10px] uppercase text-gray-500 font-bold block">URL do Website Submetido</span>
                <span className="font-mono text-gray-800 break-all">{app.url || 'Não informada'}</span>
              </div>
            </div>
          </div>

          {/* Cláusulas Principais Aceitas */}
          <div className="border border-blue-200 rounded-xl p-4 bg-blue-50/40">
            <h4 className="text-xs font-bold text-blue-900 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <FiCheckCircle className="text-blue-700" />
              Cláusulas Especiais e Obrigações Contratuais Assumidas
            </h4>
            <ul className="text-[11px] text-gray-800 space-y-1.5 leading-relaxed list-disc list-inside">
              <li>
                <strong>Fidelidade Contratual Mínima de 12 Meses:</strong> O CONTRATANTE reconhece expressamente que o contrato vige pelo prazo determinado de 12 (doze) meses de fidelidade, iniciado na confirmação do pedido, em razão dos investimentos operacionais e custos de compilação iniciais assumidos pela CONTRATADA.
              </li>
              <li>
                <strong>Multa Penal Compensatória (30%):</strong> O cancelamento voluntário ou rescisão imotivada por iniciativa do CONTRATANTE antes dos 12 meses sujeitará o mesmo à multa rescisória compensatória de 30% (trinta por cento) sobre a soma das mensalidades remanescentes.
              </li>
              <li>
                <strong>Vedação Expressa de Chargebacks:</strong> É vedada a abertura de contestações bancárias injustificadas (*chargebacks*) após início do desenvolvimento ou compilação, sujeitando o devedor à cobrança com encargos, perdas e danos.
              </li>
              <li>
                <strong>Foro de Eleição:</strong> Eleito o Foro da Comarca da sede da CONTRATADA, com renúncia expressa a qualquer outro, para dirimir eventuais litígios.
              </li>
            </ul>
          </div>

          {/* Trilha de Auditoria Digital (Evidências Técnicas) */}
          <div className="border-2 border-gray-900 rounded-xl p-4 bg-gray-50">
            <h4 className="text-xs font-black text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <FiCalendar className="text-gray-900" />
              Trilha de Auditoria Digital (Evidências Eletrônicas de Aceite)
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-[10px] uppercase text-gray-500 font-bold block">Data e Hora Exata do Aceite</span>
                <span className="font-semibold text-gray-900">{formattedDate}</span>
              </div>
              <div>
                <span className="text-[10px] uppercase text-gray-500 font-bold block">Status da Assinatura</span>
                <span className="font-bold text-green-700 flex items-center gap-1">
                  ✓ Válida, irretratável e arquivada
                </span>
              </div>
              <div>
                <span className="text-[10px] uppercase text-gray-500 font-bold block">Endereço IP Registrado</span>
                <span className="font-mono text-gray-800">{ipAddress}</span>
              </div>
              <div>
                <span className="text-[10px] uppercase text-gray-500 font-bold block">Dispositivo / Agente do Usuário</span>
                <span className="font-mono text-[10px] text-gray-700 break-all block">{userAgent}</span>
              </div>
              <div className="col-span-1 sm:col-span-2 pt-2 border-t border-gray-200">
                <span className="text-[10px] uppercase text-gray-500 font-bold block">Hash Criptográfico de Integridade</span>
                <span className="font-mono text-[11px] text-blue-900 font-bold break-all">{documentHash}</span>
              </div>
            </div>
          </div>

          {/* Assinatura / Validação Final */}
          <div className="pt-6 border-t border-gray-300 text-center">
            <p className="text-[11px] text-gray-500 uppercase tracking-widest font-semibold">
              Documento assinado digitalmente por manifestação de vontade expressa do contratante
            </p>
            <p className="text-[10px] text-gray-400 mt-1">
              Registro sob guarda eletrônica da CriaApp Soluções Tecnológicas &middot; ID Pedido: {app.id} &middot; ID Cliente: {userId}
            </p>
          </div>

        </div>

        {/* Rodapé do Modal */}
        <div className={`px-6 py-4 flex items-center justify-between border-t ${
          isDarkMode ? 'bg-gray-800/80 border-gray-700' : 'bg-gray-50 border-gray-200'
        }`}>
          <div className="text-xs text-gray-500">
            Dica: No diálogo de impressão, escolha <strong>Salvar como PDF</strong> para arquivar o documento.
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
              }`}
            >
              Fechar
            </button>
            <button
              onClick={handlePrint}
              className="inline-flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-xl shadow-md transition-all active:scale-95"
            >
              <FiPrinter className="w-4 h-4" />
              <span>Imprimir / PDF</span>
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default LegalAcceptanceModal;
